import { useTranslation } from 'react-i18next';
import { GroupListModel, ListModel, ListModelWithDetail, TranslationList, UnitTranslationList } from "shared/interfaces/core-interfaces";
import { FinanceDocumentStatuses, FinanceDocumentType, RequestStatusIds } from '../../constants/enums';
import { MRT_Column, MRT_ColumnDef, MRT_RowData, MRT_TableInstance } from 'material-react-table';
import { CreditNoteDTO, PaymentDTO } from 'features/Finance/FinanceDocuments/state/finance-document-interfaces';
import { RequestForOverdueCalculation } from 'features/TenantSettings/Requests/state/request-interfaces';
import { TextFieldProps } from '@mui/material';
import { t } from 'i18next';
import { MultiValue, SingleValue } from 'react-select';
import { SetDefaultTableViewResponse, TableViewSettings } from 'features/TableView/state/settings-interfaces';
import { PayloadAction } from '@reduxjs/toolkit';
import dayjs, { isDotNetDefaultDate } from 'shared/dayjs';
import { Activity } from 'features/Core/HomePage/state/activities-interface';
import { FileWithId } from 'features/Core/HomePage/state/notes-interfaces';
import { AppDispatch } from '../../app/store';
import { showNotification } from 'shared/slices/statusNotificationsSlice';
import { NotificationTypes } from 'shared/interfaces/status-notifications-interfaces';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import i18n from 'i18n/config';
// import { minMaxDateModel } from 'features/Finance/FinanceDashboard/state/finance-dasboard-interfaces';
import { ArticleModel, UnitModel } from 'features/CorporationSettings/referenceDataFeatures/state/reference-data-interface';

import { ObjectSchema, SchemaDescription } from 'yup';
import { TableFilterSelectIcon } from 'components/Tables/TableFilterSelectIcon';

export type ActionType = "create" | "edit" | "detail";

export type HoverOptionType = "Edit" | "Details" | "Delete" | "Deactivate" | "Reactivate";

export type ProjectClassification = "internal" | "external";

export type BatchUpdateActions = "CREATE" | "EDIT" | "DELETE" | "REORDER" | "DETAIL" | "DUPLICATE";

export type PlanningTableType = "employee" | "project";

export const ZeitonAccessToken = "AuthToken";
export const ZeitonRefreshToken = "RefreshToken";
export const ZeitonAcessTokenApp = "AuthTokenApp";
export const RememberMe = "rememberMe";

export const nullDate: Date = new Date("0000-00-00T00:00:00Z");

// Last-resort time zone when neither the browser nor the user's settings provide
// a valid IANA id. Never expected to be hit in practice, but guarantees callers
// always receive a usable zone instead of undefined/empty.
const DEFAULT_TIME_ZONE = "Europe/Berlin";

// Last-resort date format when the user's settings are unavailable (e.g. a filter running
// before the auth slice has been rehydrated). Matches the fallback used at the call sites.
const DEFAULT_DATE_FORMAT = "DD.MM.YYYY";

// Reads the logged-in user's settings from the persisted auth state.
// We read localStorage directly (redux-persist writes the auth slice under
// 'persist:local') instead of importing the store, to avoid a circular import
// (common.ts -> app/store -> slices -> common.ts) that breaks module init.
// Only for the non-React helpers below; components should select from the store.
const readPersistedUserSettings = (): any | undefined => {
    try {
        const raw = localStorage.getItem("persist:local");
        if (!raw) return undefined;
        // Each field in the persisted object is itself a JSON string.
        const settingsRaw = JSON.parse(raw)?.userSettings;
        if (!settingsRaw) return undefined;
        return typeof settingsRaw === "string" ? JSON.parse(settingsRaw) : settingsRaw;
    } catch {
        return undefined;
    }
};

// In production this is an IANA id; on a Windows dev server it may be a Windows
// id, which fails validation and falls through to the default zone below.
const getSettingsZoneName = (): string | undefined => readPersistedUserSettings()?.zoneName ?? undefined;

// The user's own moment format string, for the non-React date helpers below.
export const getUserDateFormat = (): string => {
    const format = readPersistedUserSettings()?.dateFormat;
    return format && format !== "" ? format : DEFAULT_DATE_FORMAT;
};

// True when `tz` is a non-empty, resolvable IANA time zone id.
export const isValidTimeZone = (tz: string | null | undefined): boolean => {
    if (!tz) return false;
    try {
        // Throws RangeError for unknown/invalid zone ids.
        Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
    } catch {
        return false;
    }
};

// Resolves the effective time zone with a fallback chain:
//   browser zone -> user settings (authentificationSlice.userSettings.zoneName) -> default.
// Some machines report an empty/invalid browser zone (Intl returns "" or throws),
// which used to break every `moment(...).tz(...)` call and any timeZone sent to the
// API. Always returns a valid IANA id so those never receive a bad value.
export const getUserTimeZone = (): string => {
    let browserZone: string | undefined;
    try {
        browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        browserZone = undefined;
    }
    if (isValidTimeZone(browserZone)) return browserZone!;

    const settingsZone = getSettingsZoneName();
    if (isValidTimeZone(settingsZone)) return settingsZone!;

    return DEFAULT_TIME_ZONE;
};

// Rounds a number to at most two decimals. Whole numbers and values with
// one/two decimals are returned unchanged; anything longer is rounded to 2.
export const roundToTwoDecimals = (value: number | null | undefined): number => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    return Math.round(value * 100) / 100;
};

// Rounds a number exactly the way toLocaleString(locale, options) displays it, for totals that have
// to add up to the rounded rows shown above them - pass the options the cells are formatted with.
// toLocaleString rounds the number's decimal form, so 1.005 is shown as "1.01", while Math.round works
// on its binary value and gives 1: a total built from roundToTwoDecimals would still be a cent off in
// exactly those cases. The locale only changes separators, never the rounding, so an en-US string
// that Number() can read back is used. Formatters are cached because a footer calls this per row.
const displayRoundingFormats = new Map<string, Intl.NumberFormat>();

export const roundAsDisplayed = (
    value: number | null | undefined,
    options: { minimumFractionDigits?: number, maximumFractionDigits?: number }
): number => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    const key = `${options.minimumFractionDigits}/${options.maximumFractionDigits}`;
    let format = displayRoundingFormats.get(key);
    if (format === undefined) {
        format = new Intl.NumberFormat('en-US', { ...options, useGrouping: false });
        displayRoundingFormats.set(key, format);
    }
    return Number(format.format(value));
};

// A money amount as every table shows it: exactly two decimals, in the app's language, so 1500 is
// "1,500.00" wherever it appears. Anything that is not a number (a missing amount) is " - ".
export function formatAmount(value: unknown): string {
    if (typeof value !== "number" || isNaN(value)) return " - ";
    return value.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function getCurrentDate() {
    return new Date();
}

export function ConvertDateToString(convertDate: Date, separator: string = '-') {
    let date = convertDate.getDate();
    let month = convertDate.getMonth();
    let year = convertDate.getFullYear();
    return `${year}${separator}${month < 10 ? `0${month}` : `${month}`}${separator}${date < 10 ? `0${date}` : `${date}`}`
}

export function GetUnixCurrentUTCTime() {
    let p = new Date().getTime();
    return p;
}

export function SetCulture(culture: string) {
    const { i18n } = useTranslation();
    i18n.changeLanguage(culture);
}

export function extractErrorMessageFromException(exceptionString: string): string {
    const newRegex = /Validation failed:[^:]+[\s\S]([\s\S]+?)\sSeverity:/;
    const newMatch = newRegex.exec(exceptionString);
    if (newMatch && newMatch.length > 1) {
        return newMatch[1];
    }

    const oldRegex = /System\.Exception: ([\s\S]+?) -/;
    const oldMatch = oldRegex.exec(exceptionString);
    if (oldMatch && oldMatch.length > 1) {
        return oldMatch[1];
    }

    return 'Error in Application';
}

export interface ValueResponse<T> {
    value: T;
    status: string;
    message: string;
}

export function generateListModelFromEnum<T>(enumObject: any): ListModel<T>[] {
    return Object.keys(enumObject)
        .filter(key => isNaN(Number(key)))
        .map(key => ({
            value: enumObject[key] as T,
            label: key
        }));
}

export function generateListModelFromEnumNumber(enumObject: any): ListModel<number>[] {
    const keys = Object.keys(enumObject).filter(key => isNaN(Number(key)));
    const list: ListModel<number>[] = keys.map(key => ({
        value: enumObject[key],
        label: key
    }));
    return list;
}

export function BreakString(character: string, value: string) {
    const values = value.split(character);

    for (let i = 0; i < values.length; i++) {
        values[i] = values[i] + "\n";
    }

    return values.join("");
}

export function SplitStringByUppercase(input: string) {
    var resultArray = input.split(/(?=[A-Z])/);
    return resultArray.join(" ");
}

export function GenerateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0,
            v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export const QuillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],

        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],

        [{ 'header': [1, 2, 3, 4, false] }],

        [{ 'color': [] }],
        [{ 'align': [] }],

        ['clean'],
    ],
    clipboard: {
        matchers: [
            [Node.ELEMENT_NODE, (_node: HTMLElement, delta: any) => {
                delta.ops = delta.ops.map((op: any) => {
                    if (op.attributes) {
                        delete op.attributes.background;
                        delete op.attributes.color;
                    }
                    return op;
                });
                return delta;
            }]
        ]
    }
};


export function GetTextFromHtml(html: string) {
    var divContainer = document.createElement("div");
    divContainer.innerHTML = html;
    return divContainer.textContent || divContainer.innerText || "";
}

export function downloadFile(fileName: string | undefined | null, fileB64: string | undefined | null) {
    if (fileName && fileB64) {
        const fileData = fileB64;
        const anchorElement = document.createElement('a');
        const url = 'data:application/octet-stream;base64,' + fileData;
        anchorElement.href = url;
        anchorElement.download = fileName ?? '';
        anchorElement.click();
        anchorElement.remove();
        anchorElement.setAttribute('target', '_blank')
    }
}

export function downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => {
        anchor.remove();
        URL.revokeObjectURL(url);
    }, 100);
}

export function extractVcfFullName(vcfContent: string): string {
    // The FN line may carry vCard 2.1 parameters and be quoted-printable encoded for names with
    // non-ASCII characters. The declared CHARSET differs by export format:
    //   modern: "FN;CHARSET=UTF-8;ENCODING=QUOTED-PRINTABLE:G=C3=BCnther"
    //   legacy: "FN;CHARSET=iso-8859-1;ENCODING=QUOTED-PRINTABLE:G=FCnther"
    // A long QP value is folded across lines with a trailing "=" soft line break; un-fold those
    // first, otherwise we'd capture only the first physical line and lose the tail (e.g. the
    // "=C3=9F" for "ß"), producing names like "Günther_Schröder_Wei=".
    const unfolded = vcfContent.replace(/\r\n/g, '\n').replace(/=\n/g, '');
    const fnMatch = unfolded.match(/^FN((?:;[^:]*)?):(.+)$/m);
    if (!fnMatch) return 'contact';

    const params = fnMatch[1].toUpperCase();
    let value = fnMatch[2];
    if (params.includes('QUOTED-PRINTABLE')) {
        const charsetMatch = params.match(/CHARSET=([^;]+)/);
        value = decodeQuotedPrintable(value, charsetMatch?.[1] ?? 'UTF-8');
    }
    return value.trim().replace(/\s+/g, '_') || 'contact';
}

function decodeQuotedPrintable(value: string, charset: string): string {
    const bytes: number[] = [];
    for (let i = 0; i < value.length; i++) {
        if (value[i] === '=' && i + 2 < value.length) {
            bytes.push(parseInt(value.substr(i + 1, 2), 16));
            i += 2;
        } else {
            bytes.push(value.charCodeAt(i));
        }
    }
    // TextDecoder understands both "utf-8" and "iso-8859-1"/"windows-1252" charset labels.
    return new TextDecoder(charset.toLowerCase()).decode(new Uint8Array(bytes));
}

export function base64ToBlob(base64Data: string): Blob {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray]);
}

export function filterListModels<T>(fullList: ListModel<T>[], selectedValues: T[]): ListModel<T>[] {
    const selectedItems: ListModel<T>[] = [];

    for (const value of selectedValues) {
        const selectedItem = fullList.find(item => item.value === value);
        if (selectedItem) {
            selectedItems.push(selectedItem);
        }
    }

    return selectedItems;
}

export function GetFinanceDocumentTypeName(value: string, isFullCancellation?: boolean | null): string {
    for (const [key, val] of Object.entries(FinanceDocumentType)) {
        if (val === value) {
            if (key === 'CreditNote') {
                return isFullCancellation ? t('Full Cancellation Credit Note') : t('Partial Credit Note');
            }
            return key;
        }
    }
    return "";
}

export function GetFinanceRequestStatusIds(value: string): string {
    for (const [key, val] of Object.entries(RequestStatusIds)) {
        if (val === value) {
            return key;
        }
    }
    return "";
}

export function BlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result as string;
            resolve(base64data.split(',')[1]);
        };
        reader.onerror = (error) => {
            reject(new Error("Error reading the Blob"));
        };
        reader.readAsDataURL(blob);
    });
}

export function getTranslatedName(activeArticle: ArticleModel, activeLanguage: string): string {
    // Function to get translation name based on language
    const getTranslation = (language: string) => {
        return activeArticle.translations?.find(t => t.language === language)?.translationName;
    };

    // Check for active language and return the corresponding name
    if (activeLanguage === "de" || activeLanguage === "sr") {
        const translationName = getTranslation(activeLanguage);
        if (translationName !== null && translationName !== undefined) {
            return `${activeArticle?.number}_${translationName}`;
        } else {
            return `${activeArticle?.number}_${activeArticle?.name}`;
        }
    } else {
        return `${activeArticle?.number}_${activeArticle?.name}`;
    }
};

export function dateFilterFormating(row: any, columnId: string, filterValue: any): boolean {
    const rowValue = row.getValue(columnId);

    if (!filterValue) {
        return true;
    }

    if (!(rowValue instanceof Date)) {
        return false; // If it's not a date, don't apply the filter
    }

    const filterDate = new Date(filterValue); // Convert the filter value to a Date object

    if (isNaN(filterDate.getTime())) {
        return false; // Invalid filter date
    }

    // Compare the dates without time
    return (
        rowValue.getFullYear() === filterDate.getFullYear() &&
        rowValue.getMonth() === filterDate.getMonth() &&
        rowValue.getDate() === filterDate.getDate()
    );
}

export interface PaymentStatusData {
    status: string,
    invoiceSum: number,
    endDate: Date | null,
    hasSecurityDeposit: boolean,
    depositSum: number,
    paymentSumInGrossCurrency: number,
    paymentSumInOtherCurrency: string | null,
    grossCurrency: string,
    creditedSum: number
}

export function getPaymentStatus(docStatus: string, gross: number, currency: string, payments: PaymentDTO[] | null,
    securityDeposit: boolean | null, depositPercent: number | null, endContractDate: Date | null, language: string, paidByCash?: boolean, creditNotes?: CreditNoteDTO[] | null): PaymentStatusData {
    var status = '';

    var depositSum: number = 0;
    if (securityDeposit && securityDeposit === true && depositPercent && depositPercent > 0) {
        depositSum = gross * (depositPercent / 100);
    }

    var paymentSumInOtherCurrency: string = "";
    var paymentSum = 0;
    var paymentsByCurrency: Record<string, number> = {};
    var creditNotesSum = 0;

    if (creditNotes && creditNotes.length > 0) {
        for (const creditNote of creditNotes) {
            if ([FinanceDocumentStatuses.Approved, FinanceDocumentStatuses.Sent, FinanceDocumentStatuses.Done].includes(creditNote.statusId.toString() as FinanceDocumentStatuses)) {
                creditNotesSum += creditNote.gross ?? 0;
            }
        }
    }

    if (paidByCash && paidByCash === true) {
        return {
            status: "Paid",
            hasSecurityDeposit: securityDeposit ? securityDeposit : false,
            depositSum: depositSum,
            invoiceSum: gross,
            grossCurrency: currency,
            paymentSumInGrossCurrency: gross,
            paymentSumInOtherCurrency: currency,
            endDate: endContractDate,
            creditedSum: creditNotesSum
        };
    }

    if (payments && payments.length > 0) {
        for (const payment of payments) {
            if (payment.currency === currency) {
                paymentSum += payment.amount + (payment.bankCommission ?? 0);
            } else {
                paymentsByCurrency[payment.currency] = (paymentsByCurrency[payment.currency] || 0) + payment.amount;
            }
        }

        paymentSumInOtherCurrency = Object.entries(paymentsByCurrency)
            .map(([cur, sum]) => `${sum?.toLocaleString(language, { minimumFractionDigits: 2 })} ${cur}`)
            .join(', ');
    }

    var realPaymentSum = paymentSum + creditNotesSum;

    // if (docStatus === FinanceDocumentStatuses.Disputed) {
    //     status = realPaymentSum > 0 ? "Partially paid" : "Disputed";
    // }
    /* else */
    if (docStatus === FinanceDocumentStatuses.Cancelled || docStatus === FinanceDocumentStatuses.Rejected) {
        status = realPaymentSum > 0 ? "Partially paid" : "Cancelled";
    }
    else if (realPaymentSum === 0) {
        status = "Not paid";
    }
    else if (realPaymentSum > 0) {
        if (realPaymentSum >= gross) {
            status = "Paid"
        }
        else {
            var endDate: Date | null = null;
            var today = new Date();
            today.setHours(0, 0, 0, 0);
            if (endContractDate) {
                endDate = new Date(endContractDate);
                endDate.setHours(0, 0, 0, 0);
            }

            if (securityDeposit && securityDeposit === true && depositPercent && depositPercent > 0) {
                var baseGross = gross - depositSum;
                if ((!endDate || (endDate >= today))) {
                    if (realPaymentSum >= baseGross) {
                        status = "Paid"
                    }
                    else {
                        status = "Partially paid"
                    }
                }
                else {
                    if (realPaymentSum >= gross) {
                        status = "Paid"
                    }
                    else {
                        status = "Partially paid"
                    }
                }
            }
            else {
                status = "Partially paid"
            }

        }
    }
    return {
        status: status, hasSecurityDeposit: securityDeposit ? securityDeposit : false, depositSum: depositSum, invoiceSum: gross,
        grossCurrency: currency, paymentSumInGrossCurrency: paymentSum, paymentSumInOtherCurrency: paymentSumInOtherCurrency, endDate: endContractDate,
        creditedSum: creditNotesSum
    };
}

export function getPaymentDaysOverdue(
    gross: number,
    currency: string,
    payments: PaymentDTO[] | null,
    paymentDeadline: Date | null,
    securityDeposit: boolean | null,
    depositPercent: number | null,
    endContractDate: Date | null
): number {
    if (!paymentDeadline || isNaN(new Date(paymentDeadline).getTime())) return 0;

    let paymentSum = 0;

    if (payments?.length) {
        for (const payment of payments) {
            if (payment.currency === currency) {
                paymentSum += payment.amount + (payment.bankCommission ?? 0);
            }
        }
    }

    let effectiveGross = gross;

    if (securityDeposit && depositPercent && depositPercent > 0) {
        const depositSum = gross * (depositPercent / 100);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const endDate = endContractDate ? new Date(endContractDate) : null;
        if (!endDate || !isNaN(endDate.getTime()) && endDate >= today) {
            effectiveGross -= depositSum;
        }
    }

    const isFullyPaid = paymentSum >= effectiveGross;

    if (!isFullyPaid) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const deadline = new Date(paymentDeadline);
        deadline.setHours(0, 0, 0, 0);

        const diffMs = today.getTime() - deadline.getTime();
        const daysOverdue = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        return daysOverdue > 0 ? daysOverdue : 0;
    }

    return 0;
}


export function daysOverdueCalculation(
    createdDate: Date,
    updatedDate: Date,
    docStatus: string,
    request: RequestForOverdueCalculation | null,
    docDate: Date | null,
    docDirection: string): number {
    var daysOverdue: number = 0;

    if (docStatus != null) {
        var today: Date = new Date(new Date().toUTCString());
        if (docStatus === t("Preparation") || docStatus === t("Received")) {
            var timeDifference = today.getTime() - new Date(createdDate).getTime();
            daysOverdue = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        }
        else if (docStatus === t("Correction")) {
            var timeDifference = today.getTime() - new Date(updatedDate).getTime();
            daysOverdue = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        }
        else if (docStatus === t("Approving")) {
            if (request !== null && request !== undefined) {
                if (request.statusName !== "Pending") {
                    daysOverdue = -1;
                }
                else {
                    var timeDifference = today.getTime() - new Date(request.createdDate).getTime();
                    daysOverdue = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
                }
            }
        }
        else if (docStatus === t("Approved")) {
            if (request != null && request !== undefined) {
                if (request.statusName !== "Approved") {
                    daysOverdue = -1;
                }
                else {
                    if (request.resolvedDate !== null) {
                        var timeDifference = today.getTime() - new Date(request.resolvedDate).getTime();
                        daysOverdue = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
                    }
                }
            }
        }
        else if (docStatus === t("Sent")) {
            if (docDate !== null) {
                var timeDifference = today.getTime() - new Date(docDate).getTime();
                daysOverdue = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
            }
        }
    }

    return daysOverdue;
}

export const muiFilterTextFieldProps = <T extends MRT_RowData>(
    t: (key: string) => string
): TextFieldProps | ((props: {
    column: MRT_Column<T, unknown>;
    rangeFilterIndex?: number | undefined;
    table: MRT_TableInstance<T>;
}) => TextFieldProps) | undefined => ({
    SelectProps: {
        multiple: true,
        displayEmpty: true,
        className: 'select-table-filter',
        IconComponent: TableFilterSelectIcon,
        renderValue: (value: unknown) => {
            const selected = value as string[];

            if (selected.length === 0) {
                return t('All');
            }

            return selected.map((val) => t(val)).join(', ');
        },
    },
});

// Browsers (esp. Chrome) ignore the autoComplete="new-password" MRT puts on its text inputs
// and still overlay their own saved values — e.g. an employee name suggested in a Status
// filter, because a nearby "EMPLOYEE" header makes Chrome treat the field as a name field,
// and unnamed inputs share autofill history across columns/tables. A unique, non-standard
// field name per input plus autoComplete="off" stops the browser from classifying the field
// or pooling typed values. Spread these into every useMaterialReactTable call.
export const noAutofillFilterProps = <T extends MRT_RowData>({ column }: { column: MRT_Column<T> }): TextFieldProps => ({
    name: `mrt-filter-${column.id}`,
    inputProps: {
        autoComplete: 'off',
        sx: { textOverflow: 'ellipsis' },
    },
    // MRT floors the filter field's width (see getFilterFieldMinWidth) and the header cell is
    // overflow: visible, so in a column narrower than that floor the field renders straight over
    // the next column's header. Drop the floor and let the field track its column instead. MRT
    // spreads this sx after its own, so it wins, and it reaches the plain TextField, the
    // Autocomplete's rendered input and the DatePicker alike. withFilterMinSizes below is what
    // then keeps the column wide enough for the field to still be usable; this is the backstop
    // for every table that doesn't use it.
    sx: {
        minWidth: 0,
        // MUI's own default on the Autocomplete input, which would otherwise re-impose a floor.
        '& .MuiAutocomplete-input': { minWidth: 0 },
    },
});

// _table.scss forces `padding: 0 1rem` on every MuiTableCell, so a header cell's content box is
// 32px narrower than its column. MRT lays the filter field out at `width: calc(100% + 4px)` with
// `mx: -2px` — those 2px sit inside the cell padding, so the field is effectively 4px wider than
// the content box. Range variants render two fields in a `1fr 1fr` grid with a 1rem gap
// (MRT_FilterRangeFields), and a 1fr track cannot shrink below its item's min width.
const CELL_PADDING_X = 32;
const FIELD_OVERHANG = 4;
const RANGE_GAP = 16;

// How wide each filter field should be to stay usable. These mirror MRT's own floors except for
// the range case, where MRT asks for 100px per field — two of those plus the gap and the cell
// padding would force a 240px column, which is far wider than the money columns these appear on.
const DATE_FIELD_MIN_WIDTH = 160;
const RANGE_FIELD_MIN_WIDTH = 72;
const DEFAULT_FIELD_MIN_WIDTH = 120;

/**
 * The column width needed to hold this column's own filter field, or 0 if it needs no floor.
 * Yields 148 for a plain text/autocomplete/select filter, 188 for a date filter, 184 for a range
 * and 360 for a date range.
 */
const getFilterFieldMinWidth = <T extends MRT_RowData>(columnDef: MRT_ColumnDef<T>): number => {
    const { filterVariant } = columnDef;
    // MRT_TableHeadCellFilterContainer tests both of these before its range branch, and neither
    // renders a text field at all — so they must short-circuit before the includes('range') test
    // below, or range-slider would wrongly be treated as a two-field range.
    if (filterVariant === 'checkbox' || filterVariant === 'range-slider') return 0;
    if (columnDef.enableColumnFilter === false || columnDef.columnDefType === 'display') return 0;

    const isRange = !!filterVariant?.includes('range');
    const fieldMinWidth = filterVariant?.startsWith('date') ? DATE_FIELD_MIN_WIDTH
        : isRange ? RANGE_FIELD_MIN_WIDTH
            : DEFAULT_FIELD_MIN_WIDTH;
    const contentWidth = isRange
        ? (fieldMinWidth - FIELD_OVERHANG) * 2 + RANGE_GAP
        : fieldMinWidth - FIELD_OVERHANG;
    return contentWidth + CELL_PADDING_X;
};

/**
 * Raises each column's resize floor to whatever its own filter field needs, for as long as the
 * filter row is showing. Returns the original array — and the original column defs — whenever
 * nothing needs raising, so hiding the filters is a true no-op.
 *
 * Keep the table's showColumnFilters state controlled so the floor follows the filter row, and
 * always pass the caller's own columns in rather than an already-mapped array: MRT's
 * prepareColumns mutates the defs it is handed, and the copies made here are what shield the
 * caller's memoized defs from that.
 */
export const withFilterMinSizes = <T extends MRT_RowData>(
    columns: MRT_ColumnDef<T>[],
    showColumnFilters: boolean,
    baseMinSize: number,
): MRT_ColumnDef<T>[] => {
    if (!showColumnFilters) return columns;
    let changed = false;
    const next = columns.map((columnDef) => {
        const minSize = getFilterFieldMinWidth(columnDef);
        if (minSize <= (columnDef.minSize ?? baseMinSize)) return columnDef;
        changed = true;
        return { ...columnDef, minSize };
    });
    return changed ? next : columns;
};

export const noAutofillSearchProps: TextFieldProps = {
    name: 'mrt-global-search',
    inputProps: {
        autoComplete: 'off',
    },
};

export const addSpacesEveryFourChars = (input: string | null): string => {
    if (input !== null) {
        return input.match(/.{1,4}/g)?.join(" ") || input;
    }
    else {
        return "";
    }

};

export const maskBankNumbers = (number: string | undefined | null) => {
    if (number === null || number === undefined) return '-';

    return number.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
};

export const maskBankNumbersWithName = (numberWithName: string | undefined | null) => {
    if (numberWithName === null || numberWithName === undefined) return '-';
    if (numberWithName.includes('_')) {
        var numberAndName = numberWithName.split('_');
        var name = numberAndName[0];
        var number = numberAndName[1].replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
        return name + "_" + number;
    }
    else {
        numberWithName.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
    }

};

export const overdueOptions = [
    { label: "High Overdue (10+ days)", value: "high" },
    { label: "Medium Overdue (8-10 days)", value: "medium" },
    { label: "Lite Overdue (1-7 days)", value: "lite" },
    { label: "Non Overdue (0 or less)", value: "none" }
];

export function overdueFilter(row: any, columnId: string, filterValue: string): boolean {
    const rowValue = row.getValue(columnId);

    const daysOverdue = rowValue;

    switch (filterValue) {
        case "high":
            return daysOverdue > 10;
        case "medium":
            return daysOverdue > 7 && daysOverdue <= 10;
        case "lite":
            return daysOverdue > 0 && daysOverdue <= 7;
        case "none":
            return daysOverdue <= 0;
        default:
            return true; // If no filter value, show all
    }
}

// A complete date typed into the filter, or null while it is still partial.
// Strict parsing (dayjs's third argument) is essential: in lax mode dayjs resolves partial
// input like "24" to a real date - the 24th of the current month - which would make the
// as-you-type prefix match below unreachable and silently filter on the wrong day.
const parseTypedDate = (value: string, format: string): Date | null => {
    const parsed = dayjs(value, format, true);
    return parsed.isValid() ? parsed.toDate() : null;
};

// Renders a row's date the same way the column's cell does, so the prefix match compares
// against exactly what the user sees while typing.
const formatRowDate = (date: Date, format: string): string => dayjs(date).format(format);

export const dateRangeFilter = (row: any, columnId: any, filterValue: any) => {
    if (!filterValue) return true;

    const rowDate = row.getValue(columnId);
    if (!rowDate) return false;

    const rowValue = new Date(rowDate);
    // The filter is a free-text field, so both sides use the user's own date format: what they
    // type is parsed with it, and the row's date is rendered with it for the prefix match.
    const dateFormat = getUserDateFormat();

    const resolveDate = (v: any): Date | null => {
        if (!v) return null;
        if (v.toDate) return v.toDate();
        if (typeof v !== 'string') return new Date(v);
        return parseTypedDate(v, dateFormat);
    };

    const fromStr = typeof filterValue.from === 'string' ? filterValue.from : null;
    const toStr = typeof filterValue.to === 'string' ? filterValue.to : null;
    const from = resolveDate(filterValue.from);
    const to = resolveDate(filterValue.to);

    if (fromStr && !from) {
        if (!formatRowDate(rowValue, dateFormat).startsWith(fromStr)) return false;
    } else if (from && rowValue < from) {
        return false;
    }

    if (toStr && !to) {
        if (!formatRowDate(rowValue, dateFormat).startsWith(toStr)) return false;
    } else if (to) {
        const toEndOfDay = new Date(to);
        toEndOfDay.setHours(23, 59, 59, 999);
        if (rowValue > toEndOfDay) return false;
    }

    return true;
};

dateRangeFilter.autoRemove = () => false;

export const containsTextFilter = (key: string) => (row: any, _columnId: string, filterValue: string) => {
    const value = row.original[key];

    if (typeof value === 'string') {
        return value.toLowerCase().includes(filterValue.toLowerCase());
    }

    return false;
};

export function translateDropdownOptions<T extends { label: string }>(options: T[] | [] | undefined | null) {
    let translatedOptions: T[] | [] = [];
    translatedOptions = options ? options.map(model => (model && {
        ...model,
        label: t(model.label) as string
    })) : [];
    return translatedOptions;
};

// Normalize a label/query for searching: strip diacritics the way react-select's own default
// filter does (ignoreAccents) and lower-case per the active locale, so "oster" finds "Osterreich".
const normalizeForSearch = (value: string): string => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase(i18n.language);

// A "word character" for word-boundary detection, any Unicode letter or number. Anything else
// (spaces, hyphens, parentheses, apostrophes) counts as a boundary where a new word can start.
const isWordChar = (char: string): boolean => /[\p{L}\p{N}]/u.test(char);

/**
 * Score how well `text` matches an already-normalized `input`, lower is better:
 *   0 - the whole text starts with the input        ("Togo"     for "to")
 *   1 - a later word within the text starts with it  ("...Tobago" for "to")
 *   2 - the text contains the input somewhere else   ("esTOnia"  for "to")
 *   3 - no match
 * `input` must already be run through normalizeForSearch; `text` is normalized here.
 */
const rankOfText = (text: string, input: string): number => {
    const normalized = normalizeForSearch(text ?? '');
    if (normalized.startsWith(input)) return 0;
    for (let i = 1; i < normalized.length; i++) {
        if (!isWordChar(normalized[i - 1]) && isWordChar(normalized[i]) && normalized.startsWith(input, i)) {
            return 1;
        }
    }
    return normalized.includes(input) ? 2 : 3;                     // contains the query anywhere, else no match
};

/**
 * Rank react-select options for a search box so that, for the typed query, options whose label
 * (or any word within it) *starts with* the query come first, and options that merely *contain*
 * the query appear below them
 * Non-matches are dropped; an empty query returns every option in its original order.

 * react-select's `filterOption` can only include/exclude, never reorder, so ranking has to happen on
 * the options array itself
 */
export function rankFilterOptions<T extends { label: string }>(options: T[] | null | undefined, rawInput: string): T[] {
    return rankFilterItems(options ?? [], rawInput, option => [option.label]);
}

/**
 * The same ranking for any list searched by one or more of its texts (`textsOf`: a location's name,
 * city and managers). An item ranks by its best-matching text, items that match none are dropped,
 * and items of equal rank keep their order. An empty query returns the list itself.
 */
export function rankFilterItems<T>(items: T[], rawInput: string, textsOf: (item: T) => readonly (string | null | undefined)[]): T[] {
    const input = normalizeForSearch(rawInput.trim());
    if (!input) return items;

    return items
        .map((item, index) => ({ item, index, rank: Math.min(3, ...textsOf(item).map(text => (text ? rankOfText(text, input) : 3))) }))
        .filter(entry => entry.rank < 3)
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(entry => entry.item);
}

/** One active text column filter: the column's accessor id and the raw text the user typed. */
export interface RankTextFilter {
    id: string;
    value: string;
}

/**
 * This only *reorders*, it never drops rows. MRT's own `contains` filterFn still removes the rows that match no active filter
 * `getCellValue(row, id)` reads the raw cell value for a column
 */
export function rankFilterRows<T>(
    rows: T[],
    filters: RankTextFilter[],
    getCellValue: (row: T, id: string) => unknown,
): T[] {
    const active = filters
        .map(f => ({ id: f.id, input: normalizeForSearch(String(f.value ?? '').trim()) }))
        .filter(f => f.input !== '');
    if (active.length === 0) return rows;

    return rows
        .map((row, index) => ({
            row,
            index,
            ranks: active.map(f => rankOfText(String(getCellValue(row, f.id) ?? ''), f.input)),
        }))
        .sort((a, b) => {
            for (let i = 0; i < a.ranks.length; i++) {
                if (a.ranks[i] !== b.ranks[i]) return a.ranks[i] - b.ranks[i];
            }
            return a.index - b.index;
        })
        .map(entry => entry.row);
}

export function translateGroupDropdownOptions<T>(options: GroupListModel<T>[] | null | undefined): GroupListModel<T>[] {
    if (!options) return [];

    return options.map(group => ({
        ...group,
        label: t(group.label) as string,
        options: group.options.map(option => ({
            ...option,
            label: t(option.label) as string
        }))
    }));
}

export function translateDropdownWithDetailsOptions(options: ListModelWithDetail<any>[] | [] | undefined | null) {
    let translatedOptions: ListModelWithDetail<any>[] | [] = [];
    translatedOptions = options ? options.map(model => (model && {
        ...model,
        label: t(model.label) as string,
        detail: model.detail ? t(model.detail) as string : ""
    })) : [];

    translatedOptions.sort((a, b) =>
        a.label.localeCompare(b.label, i18n.language, { numeric: true })
    );

    return translatedOptions
};

export function translateDropdownValue(selectedModel: SingleValue<ListModel<any> | undefined>) {
    if (!selectedModel) return null;
    let translatedValue: SingleValue<ListModel<any>> | null = (selectedModel ? {
        ...selectedModel,
        label: t(selectedModel.label) as string
    } : null)
    return translatedValue;
};

export function translateDropdownWithDetailsValue(selectedModel: ListModelWithDetail<any>) {
    let translatedValue: ListModelWithDetail<any> | null = (selectedModel ? {
        ...selectedModel,
        label: t(selectedModel.label) as string
    } : null)
    return translatedValue;
};

export function translateMultiDropdownValues(selectedModels: MultiValue<ListModel<any>> | undefined | null): MultiValue<ListModel<any>> {
    if (!selectedModels) return [];
    return selectedModels.map(model => ({
        ...model,
        label: t(model.label) as string
    }));
}

export function translateDropdownDefaultValue(options: ListModel<any>[] | [] | undefined, defaultValue: any, searchBy: boolean) {
    if (!options) {
        return;
    }
    return options
        .map(model => ({
            ...model,
            label: t(model.label) as string
        }))
        .find(i => searchBy ? i.value === defaultValue : i.label === defaultValue) || null;
}

export const translateOptions = (options: TranslationList[], currentLanguage: string) => {
    return options.map(option => {
        const translation = option.names.find(
            name => name.language === currentLanguage && name.translation?.trim()
        );
        const translationOnDefLen = option.names.find(
            name => name.language === option.defaultLanguage && name.translation?.trim()
        );
        return {
            value: option.id,
            label: translation?.translation || translationOnDefLen?.translation!,
            isActive: option.isActive ?? true,
            type: option.type ?? null,
            boolValue: option.boolValue ?? null
        };
    }).sort((a, b) => a.label.localeCompare(b.label, currentLanguage));
};

export const translateUnits = (options: UnitModel[], currentLanguage: string): UnitTranslationList[] => {
    return options.map(option => {
        const translations = option.translations ?? [];

        // Pronađi prevod za trenutno izabrani jezik
        const translation = translations.find(
            t => t.language === currentLanguage && t.translationName?.trim()
        );

        // Kreiraj rezultat uz fallback vrednosti
        const translatedList: UnitTranslationList = {
            value: option.id ?? 0,
            label: translation?.translationName?.trim()
                ?? option.name,

            fullName: translation?.translationFullName?.trim()
                ?? option.fullName?.trim()
                ?? "",
            isActive: option.isActive ?? true
        };

        return translatedList;
    });
};

export const translateUnit = (option: UnitModel, currentLanguage: string) => {
    if (!option) return "";
    if (option.defaultLanguage === currentLanguage) return option.name;

    const translation = option.translations?.find(
        name => name.language === currentLanguage && name.translationName?.trim()
    );
    /*     const translationOnDefLen = option.translations?.find(
            name => name.language === option.defaultLanguage && option.name?.trim()
        ); */
    return translation?.translationName || option.name;
};

export const translateOption = (option: TranslationList, currentLanguage: string) => {
    const translation = option.names.find(
        name => name.language === currentLanguage && name.translation?.trim()
    );
    const translationOnDefLen = option.names.find(
        name => name.language === option.defaultLanguage && name.translation?.trim()
    );

    return translation?.translation || translationOnDefLen?.translation!;
};

export const findSelectedOption = (options: { value: string; label: string }[], selectedId: string | null) => {
    return options.find(option => option.value === selectedId) || null;
};

export function debounce(func: Function, delay: number) {
    let timer: NodeJS.Timeout;
    return function (...args: any[]) {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
}

export const getTableLocalization = () => {
    const lang = i18n.language;

    const customEnglishLocalization = {
        ...MRT_Localization_EN,
        rowActions: 'Row actions',
        sortByColumnAsc: 'Sort by {column} ascending',
        sortedByColumnAsc: 'Sort by {column} descending',
        sortedByColumnDesc: 'Reset to default order',
    };

    const customGermanLocalization = {
        ...MRT_Localization_EN,
        rowActions: 'Row actions',
        sortByColumnAsc: '{column} aufsteigend sortieren',
        sortedByColumnAsc: '{column} absteigend sortieren',
        sortedByColumnDesc: 'Standardreihenfolge wiederherstellen',
    };

    const customSerbianLocalization = {
        ...MRT_Localization_EN,
        rowActions: 'Row actions',
        sortByColumnAsc: 'Sortiraj po {column} rastuće',
        sortedByColumnAsc: 'Sortiraj po {column} opadajuće',
        sortedByColumnDesc: 'Vrati na podrazumevani redosled',
    };

    const baseLocalization =
        lang === 'de' ? customGermanLocalization :
            lang === 'sr' ? customSerbianLocalization :
                customEnglishLocalization;

    // Through the app's translations: the tooltip on a filter field's clear button, the one on a
    // filtered column's header - without MRT's {filterType}, the internal filter mode ("Fuzzy") -
    // and the buttons of the Show/Hide columns menu.
    return {
        ...baseLocalization,
        clearFilter: i18n.t('Clear filter'),
        filteringByColumn: i18n.t('Filtering by {column} - {filterValue}'),
        hideAll: i18n.t('Hide all'),
        resetOrder: i18n.t('Reset order'),
        showAll: i18n.t('Show all'),
    }
}

// export function setDefaultTableView(tableViewSettings: TableViewSettings | null, action: PayloadAction<SetDefaultTableViewResponse>) {
//     const tableViewNameToFalseIndex = tableViewSettings?.tableViewNames.findIndex(x => x.isDefault = true)
//     if (tableViewNameToFalseIndex !== -1 && tableViewNameToFalseIndex) {
//         tableViewSettings!.tableViewNames[tableViewNameToFalseIndex]!.isDefault = false;
//     }

//     const tableViewToTrueIndex = tableViewSettings?.tableViewNames.findIndex(
//         x => x.tableViewId === action.payload.tableViewId
//     );

//     if (tableViewToTrueIndex !== -1 && tableViewToTrueIndex) {
//         tableViewSettings!.tableViewNames[tableViewToTrueIndex]!.isDefault = true
//     }
// }
export function setDefaultTableView(
    tableViewSettings: TableViewSettings | null,
    action: PayloadAction<SetDefaultTableViewResponse>
) {
    if (!tableViewSettings) return;

    // Find the index of the currently default table view and set it to false
    const currentDefaultIndex = tableViewSettings.tableViewNames.findIndex(
        x => x.isDefault === true
    );
    if (currentDefaultIndex !== -1) {
        tableViewSettings.tableViewNames[currentDefaultIndex].isDefault = false;
    }

    // Find the index of the new table view to set as default and set it to true
    const newDefaultIndex = tableViewSettings.tableViewNames.findIndex(
        x => x.tableViewId === action.payload.tableViewId
    );
    if (newDefaultIndex !== -1) {
        tableViewSettings.tableViewNames[newDefaultIndex].isDefault = true;
    }
}

// Effectively a no-op: a valid instance never formats to 'Invalid date', so this
// returns true for any string, including garbage. Kept as-is deliberately — making
// it actually validate would change which format string wins and so change output.
const isValidDateFormat = (format: string): boolean => {
    try {
        return dayjs().format(format) !== 'Invalid date';
    } catch (e) {
        return false;
    }
};

export const formatDate = (
    date: string | Date | undefined | null,
    format: string | undefined,
    fallbackFormat: string = 'DD.MM.YYYY'
): string => {
    if (!date || isDotNetDefaultDate(date)) {
        return "-";
    }

    const validFormat = format && isValidDateFormat(format) ? format : fallbackFormat;
    return dayjs(date).tz(getUserTimeZone()).format(validFormat);
};

export const formatTime = (dateString: string | undefined, timeFormat: string | undefined): string => {
    if (!dateString || isDotNetDefaultDate(dateString)) return "-";
    const tz = getUserTimeZone();
    const formattedTime = dayjs(dateString).tz(tz).format(timeFormat || 'HH:mm');
    return formattedTime;
};

export const formatDateTime = (
    dateTime: string | Date | undefined | null,
    dateFormat: string | undefined,
    timeFormat: string | undefined,
): string => {
    if (isDotNetDefaultDate(dateTime)) return "-";
    const dateTimeFormat: string = `${dateFormat?.toString()} ${timeFormat?.toString()}`
    const validFormat = dateTimeFormat && isValidDateFormat(dateTimeFormat) ? dateTimeFormat : 'DD.MM.YYYY HH:mm';

    return dayjs(dateTime).tz(getUserTimeZone()).format(validFormat);
};

export const formatDateTimeAndConvertInLocalTime = (
    dateTime: string | Date | undefined | null,
    dateFormat?: string,
    timeFormat?: string,
): string => {
    if (!dateTime || isDotNetDefaultDate(dateTime)) return '';

    // Kombinuj format
    const dateTimeFormat = `${dateFormat || 'DD.MM.YYYY'} ${timeFormat || 'HH:mm'}`;

    // Validacija formata, ako imaš svoju `isValidDateFormat`, koristi je
    const validFormat = isValidDateFormat?.(dateTimeFormat)
        ? dateTimeFormat
        : 'DD.MM.YYYY HH:mm';

    // Dobavi lokalnu vremensku zonu (npr. "Europe/Belgrade")
    const timeZone = getUserTimeZone();

    // Konvertuj u lokalnu vremensku zonu i formatiraj
    return dayjs.utc(dateTime).tz(timeZone).format(validFormat);
};

export const formatDateAndConvertInLocalTime = (
    dateTime: string | Date | undefined | null,
    dateFormat?: string,
): string => {
    if (!dateTime || isDotNetDefaultDate(dateTime)) return ' - ';

    // Kombinuj format
    const dateTimeFormat = `${dateFormat || 'DD.MM.YYYY'}`;

    // Validacija formata, ako imaš svoju `isValidDateFormat`, koristi je
    const validFormat = isValidDateFormat?.(dateTimeFormat)
        ? dateTimeFormat
        : 'DD.MM.YYYY';

    // Dobavi lokalnu vremensku zonu (npr. "Europe/Belgrade")
    const timeZone = getUserTimeZone();

    // Konvertuj u lokalnu vremensku zonu i formatiraj
    return dayjs.utc(dateTime).tz(timeZone).format(validFormat);
};

export const blurActiveElement = () => {
    setTimeout(() => {
        (document.activeElement as HTMLElement)?.blur();
    }, 0);
};
export function calculateMonth(activity: Activity, activities: Activity[] | undefined, index: number): { previousCreatedAt: number, year: number, currentMonth: number } {
    const year = activity.createdAt
        ? new Date(activity.createdAt).getFullYear()
        : 0;
    const currentMonth = activity.createdAt
        ? new Date(activity.createdAt).getMonth()
        : 0;

    let previousCreatedAtDate = 0;

    if (activities && index > 0) {
        const createdAt = activities[index - 1]?.createdAt;
        if (createdAt) {
            const date = new Date(createdAt);
            previousCreatedAtDate = !isNaN(date.getTime()) ? date.getMonth() : 0;
        } else {
            previousCreatedAtDate = 0;
        }
    }

    return { previousCreatedAt: previousCreatedAtDate, year, currentMonth };
}

export const colors = [
    {
        value: 1,
        label: "var(--p-red-450)"
    },
    {
        value: 2,
        label: "var(--p-orange-500)"
    },
    {
        value: 3,
        label: "var(--p-green-600)"
    },
    {
        value: 4,
        label: "var(--p-white)"
    },
]

export const reminderTypes: ListModel<any>[] = [
    {
        "value": 1,
        "label": "Daily"
    },
    {
        "value": 2,
        "label": "Weekly"
    },
    {
        "value": 3,
        "label": "Monthly"
    }
]

export const deadlineTypes: ListModel<any>[] = [
    {
        "value": 1,
        "label": "Today"
    },
    {
        "value": 2,
        "label": "Tomorrow"
    },
    {
        "value": 3,
        "label": "In 2 business days"
    },
    {
        "value": 4,
        "label": "In 3 business days"
    },
    {
        "value": 5,
        "label": "In 1 week"
    },
    {
        "value": 6,
        "label": "In 2 weeks"
    },
    {
        "value": 7,
        "label": "In 1 month"
    },
    {
        "value": 8,
        "label": "In 3 months"
    },
    {
        "value": 9,
        "label": "In 6 months"
    },
    {
        "value": 10,
        "label": "Custom date"
    }
]

//Task
export const deadlineTaskOptions: ListModel<number>[] = [
    { value: 1, label: 'In one business day' },
    { value: 2, label: 'In two business days' },
    { value: 3, label: 'In three business days' },
    { value: 4, label: 'In one week' },
    { value: 5, label: 'In two weeks' },
    { value: 6, label: 'In one month' },
    { value: 7, label: 'In two months' },
];

export const imageExtensions = [
    "jpg", "jpeg", "png", "gif", "bmp", "webp", "svg",
    "heic", "heif", "tiff", "tif", "ico"
];

export const isImage = (fileWithId: FileWithId) => {
    const extension = fileWithId.file.name.split(".").pop()?.toLocaleLowerCase()
    if (extension && imageExtensions.includes(extension)) {
        return fileWithId;
    }
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, dispatch: AppDispatch, setValidFiles: React.Dispatch<React.SetStateAction<File[]>>, setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>) => {
    let notificationTriggered = false;
    if (event.target.files) {
        const filesArray = Array.from(event.target.files);

        const validFiles = filesArray.filter(file => {
            if (file.size > MAX_FILE_SIZE) {
                if (!notificationTriggered) {
                    notificationTriggered = true;
                    dispatch(showNotification({ message: "File size is bigger than 10MB", type: NotificationTypes.Info }))
                }
                //dispatch(showNotification({ message: "File size is bigger than 50MB", type: NotificationTypes.Info }))
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        setValidFiles(validFiles);
        setSelectedFiles(prevFiles => [...validFiles, ...prevFiles]);
    }
};

export const taskType: "Contact" | "Company" = "Contact";

//Profile
export function getInitialsFromName(firstName: string, lastName: string) {
    return (firstName[0] + lastName[0]).toUpperCase();
}

export function getInitialsFromCompany(companyName: string) {
    const words = companyName.trim().split(/\s+/);
    return words.length > 1
        ? words.slice(0, 2).map(word => word[0]).join('').toUpperCase()
        : words[0].slice(0, 2).toUpperCase();
}

export function getInitialsFromFullName(fullName: string) {
    const words = fullName.trim().split(/\s+/);
    return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
};

export function getFormatedNumber(num: number, maxCharacters: number, culture: string) {
    const integerPart = Math.abs(Math.trunc(num)).toString();
    const digitCount = integerPart.length;

    var formattedNumber: string = num?.toLocaleString(culture, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    if (digitCount > maxCharacters) {
        const separator = culture === "en" ? "," : ".";
        const maxLineLength = maxCharacters + Math.floor((maxCharacters - 1) / 3) + 3;

        const groups = formattedNumber.split(separator);
        const lines: string[] = [];
        let currentLine = "";

        for (let i = 0; i < groups.length; i++) {
            const piece = groups[i] + (i < groups.length - 1 ? separator : "");

            if (currentLine.length > 0 && currentLine.length + piece.length > maxLineLength) {
                lines.push(currentLine);
                currentLine = "";
            }

            currentLine += piece;
        }

        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        return lines.join("\n");
    }

    return formattedNumber;
}

export function getFormatedDecimalOrWholeNumber(num: number, maxCharacters: number, culture: string) {
    if (num === null || num === undefined || isNaN(num)) return "";

    const integerPart = Math.abs(Math.trunc(num)).toString();
    const digitCount = integerPart.length;

    const hasDecimal = Math.abs(num % 1) > 0;

    // Formatiraj broj u zavisnosti od toga da li ima decimale
    const formattedNumber = num.toLocaleString(culture, {
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: hasDecimal ? 2 : 0,
    });

    // Ako je broj "dugačak", prelomi ga na više redova tako da svaki red stane u kolonu
    if (digitCount > maxCharacters) {
        const separator = culture === "en" ? "," : ".";
        const maxLineLength = maxCharacters + Math.floor((maxCharacters - 1) / 3) + (hasDecimal ? 3 : 0);

        const groups = formattedNumber.split(separator);
        const lines: string[] = [];
        let currentLine = "";

        for (let i = 0; i < groups.length; i++) {
            const piece = groups[i] + (i < groups.length - 1 ? separator : "");

            if (currentLine.length > 0 && currentLine.length + piece.length > maxLineLength) {
                lines.push(currentLine);
                currentLine = "";
            }

            currentLine += piece;
        }

        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        return lines.join("\n");
    }

    return formattedNumber;
}

export function getDearText(hasResponsiblePerson: boolean, firstName: string | null, lastName: string | null, gender: string | null, suffixTitles: string[] | null, language: string) {
    if (!hasResponsiblePerson) {
        return `${t('Dear', { lng: language }) + ","}`;
    }

    let title = "";
    if (gender && gender === "Male") {
        if (language === 'de' && ((suffixTitles !== null && suffixTitles?.includes("Last name")) || suffixTitles === null)) {
            title = 'Sehr geehrter Herr';
        }
        else {
            title = t('Dear Mr.', { lng: language });
        }

    } else if (gender && gender === "Female") {
        if (language === 'de' && ((suffixTitles !== null && suffixTitles?.includes("Last name")) || suffixTitles === null)) {
            title = 'Sehr geehrte Frau';
        }
        else {
            title = t('Dear Ms.', { lng: language });
        }
    }
    else {
        title = t('Dear', { lng: language });
    }

    if (suffixTitles !== null && suffixTitles?.includes("First name") && suffixTitles?.includes("Last name")) {
        title = title + " " + firstName + " " + lastName;
    }
    else if (suffixTitles !== null && suffixTitles?.includes("First name")) {
        title = title + " " + firstName;
    }
    else if (suffixTitles !== null && suffixTitles?.includes("Last name")) {
        title = title + " " + lastName;
    }
    else {
        title = title + " " + lastName;
    }

    return title + ", ";
};

export function getOnHandText(hasResponsiblePerson: boolean, firstName: string | null, lastName: string | null, gender: string | null, language: string) {
    if (!hasResponsiblePerson) {
        return ``;
    }

    let title = `${t('Attn.', { lng: language })}`;
    if (gender && gender === "Male" && (language === 'de' || language === 'en')) {
        title = title + ` ${t('Mr.', { lng: language })}`;
    }
    else if (gender && gender === "Female" && (language === 'de' || language === 'en')) {
        title = title + ` ${t('Ms.', { lng: language })}`;
    }

    return title + " " + firstName + " " + lastName;
};

export const generateWeeks = (dateFrom: Date, dateTo: Date) => {
    const weeks: { week: number; year: number; startDate: string; endDate: string }[] = [];
    let from = new Date(dateFrom);
    let to = new Date(dateTo);

    // Uverite se da su datumi u validnom formatu
    if (from > to) {
        throw new Error("Date 'from' must be before 'to'.");
    }

    var lastDayOfWeek = getLastDayOfWeek(dateTo);
    while (from <= lastDayOfWeek) {
        const { week, year, startDate, endDate } = getWeekNumberAndDates(from);

        // Dodajemo nedelju samo ako nije već dodata (ne dupliramo nedelje)
        if (!weeks.some(w => w.week === week && w.year === year)) {
            weeks.push({ week, year, startDate, endDate });
        }

        // Prelazak na sledeću nedelju (dodavanje 7 dana)
        from.setDate(from.getDate() + 7);
    }

    return weeks;
};

const getWeekNumberAndDates = (date: Date): { week: number; year: number; startDate: string; endDate: string } => {
    const tempDate = new Date(date);
    tempDate.setHours(0, 0, 0, 0);

    // Pronađi četvrtak u toj nedelji (ISO standard)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));

    // Pronađi prvi četvrtak u godini
    const firstThursday = new Date(tempDate.getFullYear(), 0, 4);
    firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));

    // Izračunavanje broja nedelje
    const weekNumber = Math.round(((tempDate.getTime() - firstThursday.getTime()) / 86400000) / 7) + 1;

    // Početak i kraj nedelje
    const startDate = new Date(tempDate);
    startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);

    return {
        week: weekNumber,
        year: tempDate.getFullYear(),
        startDate: formatDatee(startDate),
        endDate: formatDatee(endDate),
    };
};

export const getYearAndWeek = (date: Date): { year: number; week: number } => {
    const tempDate = new Date(date);
    tempDate.setHours(0, 0, 0, 0);

    // Pronađi četvrtak te nedelje
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));

    // Pronađi prvi četvrtak u godini
    const firstThursday = new Date(tempDate.getFullYear(), 0, 4);
    firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));

    // Izračunavanje broja nedelje
    const weekNumber = Math.round(((tempDate.getTime() - firstThursday.getTime()) / 86400000) / 7) + 1;

    return { year: tempDate.getFullYear(), week: weekNumber };
};

const formatDatee = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}/${month}`;
};

export const getCurrentWeekAndYear = () => {
    const now = new Date();
    const firstJan = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - firstJan.getTime()) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + firstJan.getDay() + 1) / 7);
    return { currentWeek: week, currentYear: now.getFullYear() };
};

export function dueDaysTableCalculation(deadline: Date) {
    const paymentDeadLine = new Date(deadline);
    const today = new Date();
    // Set the hours to 00:00:00 to ignore the time part for today's date
    today.setHours(0, 0, 0, 0);
    const paymentDeadlineDate = new Date(paymentDeadLine);
    // Set hours to 00:00:00 for the deadline date as well
    paymentDeadlineDate.setHours(0, 0, 0, 0);
    const diffInMs: number = today.getTime() - paymentDeadlineDate.getTime();
    const daysOverdue: number = Math.floor(diffInMs / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
    return daysOverdue >= 0 ? daysOverdue : 0;
}

export function plusMinusDaysCalculation(deadline: Date) {
    if (deadline !== null) {
        const paymentDeadLine = new Date(deadline);

        const today = new Date();
        // Set the hours to 00:00:00 to ignore the time part for today's date
        today.setHours(0, 0, 0, 0);

        const paymentDeadlineDate = new Date(paymentDeadLine);
        // Set hours to 00:00:00 for the deadline date as well
        paymentDeadlineDate.setHours(0, 0, 0, 0);

        const diffInMs: number = today.getTime() - paymentDeadlineDate.getTime();
        const daysOverdue: number = Math.floor(diffInMs / (1000 * 60 * 60 * 24)); // Convert milliseconds to days

        return daysOverdue >= 0 ? daysOverdue : 0;
    } else {
        return " - "
    }
}

export const getYearOptions = (startYear: number): ListModel<string>[] => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
        const year = (startYear + i).toString();
        return { value: year, label: year };
    }).reverse();
};

export function truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
}

// //Calculate working days
// export const calculateInclusiveBusinessDays = (start: Date, end: Date): number => {
//     const startDate = new Date(new Date(start).toDateString());
//     const endDate = new Date(new Date(end).toDateString());

//     let current = startDate;
//     let count = 0;

//     while (current <= endDate) {
//         if (![0, 6].includes(dayjs(current).day())) { // 0 = Sunday, 6 = Saturday
//             count++;
//         }
//         current = dayjs(current).add(1, 'day').toDate();
//     }

//     return count;
// };

// //CalculateWeeks
// export const calculateDecimalWeeks = (startDate: Date, endDate: Date): number => {
//     const msInDay = 1000 * 60 * 60 * 24;
//     const diffInMs = endDate.getTime() - startDate.getTime();
//     const diffInDays = diffInMs / msInDay + 1; // +1 da uključimo i startDate
//     return parseFloat((diffInDays / 7).toFixed(2)); // npr. 2.57 nedelja
// };

// export const findMinStartDateAndMaxEndDate = (list: any[]): minMaxDateModel | null => {
//     let minStartDate: Date | null = null;
//     let maxEndDate: Date | null = null;
//     const intervalDurations: number[] = [];

//     for (const item of list) {
//         const start = item.startDate ? new Date(item.startDate) : null;
//         const end = item.endDate ? new Date(item.endDate) : null;

//         if (start && !isNaN(start.getTime())) {
//             if (!minStartDate || start < minStartDate) {
//                 minStartDate = start;
//             }
//         }

//         if (end && !isNaN(end.getTime())) {
//             if (!maxEndDate || end > maxEndDate) {
//                 maxEndDate = end;
//             }
//         }

//         if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
//             const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
//             intervalDurations.push(duration);
//         }
//     }

//     if (!minStartDate || !maxEndDate || intervalDurations.length === 0) {
//         return null;
//     }

//     // Ukloni prvi i poslednji segment ako ima više od 2 totalna
//     const coreDurations =
//         intervalDurations.length > 2
//             ? intervalDurations.slice(1, -1)
//             : intervalDurations;

//     const all15 = coreDurations.every(d => d === 15);
//     const allMonthly = coreDurations.every(d => d >= 28 && d <= 31);

//     let interval: number;
//     if (allMonthly) {
//         interval = 1;
//     } else if (all15) {
//         interval = 2;
//     } else {
//         interval = 0; // Ne može sa sigurnošću da detektuje
//     }

//     return {
//         minStartDate,
//         maxEndDate,
//         interval
//     };
// };

const getLastDayOfWeek = (input: Date | string): Date => {
    const date = new Date(input); // Osigurava da je pravi Date objekat

    const day = date.getDay(); // 0 (nedelja) - 6 (subota)
    const isoDay = day === 0 ? 7 : day; // ISO: ponedeljak = 1, nedelja = 7

    const lastDay = new Date(date);
    lastDay.setDate(date.getDate() + (7 - isoDay));
    return lastDay;
};

// Label optional and null-safe: reference data can reach this before its label has been resolved
// out of the translations it carries (see resolveTranslatedOption), and a missing one must sort
// rather than throw.
export function sortByLabel<T extends { label?: string }>(array?: T[], descending?: boolean): T[] {
    if (!array) return [];
    return [...array].sort((a, b) =>
        descending
            ? (b.label ?? '').localeCompare(a.label ?? '')
            : (a.label ?? '').localeCompare(b.label ?? '')
    );
}

export const stripHtml = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent?.trim() || '';
};

// Plain text of an HTML string, for searching. Uses DOMParser rather than innerHTML on a live
// element: a parsed document runs no scripts and loads no images, so user-authored HTML
// (e.g. an <img onerror>) cannot execute just by being searched.
export const htmlToPlainText = (html: string | null | undefined): string => {
    if (!html) return '';
    return new DOMParser().parseFromString(html, "text/html").body.textContent || '';
};

export interface ArticleTranslationModel {
    id: string;
    defaultLanguage: string;
    names: { language: string; translation: string }[];
}

// `articleTranslation` used to live here: it resolved an article's label for the active language,
// falling back to the article's own default. `resolveTranslatedOption` in
// `shared/utils/translated-options.ts` does that for every kind of reference data now, and the
// selects call it themselves, so the last call site went with the migration. The interface above
// stays - `deal-interfaces.ts` types its articles with it.

export function settingTableView<T extends MRT_RowData>(columns: MRT_ColumnDef<T>[]) {
    let initialTableViewSettings: {
        columnVisibility: { [key: string]: boolean },
        columnSizing: { [key: string]: number },
        columnOrder: string[]
    } = {
        columnVisibility: {},
        columnSizing: {},
        columnOrder: []
    };
    const visit = (cols: MRT_ColumnDef<T>[]) => {
        cols.forEach((column) => {
            const nested = (column as any).columns as MRT_ColumnDef<T>[] | undefined;
            if (nested && nested.length > 0) {
                visit(nested);
                return;
            }
            const key = (column.accessorKey ?? (column as any).id) as string | undefined;
            if (key !== undefined) {
                initialTableViewSettings.columnVisibility[key] = (column as any).isVisible ?? true;
                initialTableViewSettings.columnOrder.push(key);
                initialTableViewSettings.columnSizing[key] = (column as any).size ?? 100;
            }
        });
    };
    visit(columns);
    return initialTableViewSettings;
}

export const sortLikeExcel = (rowA: any, rowB: any, columnId: string) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);

    const isNumeric = (val: string) => /^[0-9]+$/.test(val);

    const aIsNum = isNumeric(a);
    const bIsNum = isNumeric(b);

    if (aIsNum && !bIsNum) return -1;
    if (!aIsNum && bIsNum) return 1;

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

export const capitalizeFirstLetter = (text: string) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export function formatTranslatedValues<T>(
    values: ListModel<T | null>[] = [],
    allItems: TranslationList[] = [],
    currentLanguage: string,
    defaultLanguage?: string,
    valueFormatter?: (val: T | null) => string,
    separator: string = ", "
): string {
    return values
        .filter(valItem => valItem.value !== null && valItem.value !== undefined) // ukloni null/undefined
        .map(valItem => {
            const key = String(valItem.value); // ID entiteta
            const item = allItems.find(a => a.id === key);

            // Prevod: prvo trenutni jezik, pa default, pa prvi u listi, pa id
            const label =
                item?.names.find(n => n.language === currentLanguage)?.translation ??
                item?.names.find(n => n.language === (defaultLanguage ?? item?.defaultLanguage))?.translation ??
                item?.names[0]?.translation ??
                key;

            return `${label}`;
        })
        .join(separator);
}

export function getBase64FileSize(base64: string): number {
    const cleaned = base64.split(',').pop() ?? '';
    const padding = (cleaned.match(/=*$/) || [''])[0].length;
    return (cleaned.length * 3) / 4 - padding;
}

export const getTodayDateUTC = () => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

export function convertIndustrialTimeToHoursMinutes(industrialTime: number | null | undefined): string {
    if (industrialTime === null || industrialTime === undefined) {
        return "00:00";
    }
    const isNegative = industrialTime < 0;
    const absTime = Math.abs(industrialTime);
    let hours = Math.floor(absTime);
    let minutes = Math.round((absTime - hours) * 60);

    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }

    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    return isNegative && formattedTime !== '00:00' ? `-${formattedTime}` : formattedTime;
}

/* Reads the `.max(n)` character limit declared for a field on a Yup object schema */
export function maxLengthFor(schema: ObjectSchema<any>, fieldName: string): number | undefined {
    const field = schema.describe().fields[fieldName];
    const tests = (field as SchemaDescription)?.tests ?? [];
    const max = tests.find((test) => test.name === 'max')?.params?.max;
    return typeof max === 'number' ? max : undefined;
}